<template>
  <div class="start-setup">
    <div class="start-setup__saddle">
      <h3>{{ t('common.bike.saddle') }}</h3>
      <h4>{{ t('common.position.index') }}</h4>
      <div class="start-setup__row">
        <span>{{ t('common.position.x') }}</span>
        <span v-if="startPosition.saddleX">{{
          `${startPosition.saddleX} ${t('common.unit.mm')}`
        }}</span>
        <span v-else>-</span>
      </div>
      <div class="start-setup__row">
        <span>{{ t('common.position.y') }}</span>
        <span v-if="startPosition.saddleY">{{
          `${startPosition.saddleY} ${t('common.unit.mm')}`
        }}</span>
        <span v-else>-</span>
      </div>
    </div>

    <div class="start-setup__handlebar">
      <h3>{{ t('common.bike.handlebar') }}</h3>
      <h4>{{ t('common.position.index') }}</h4>
      <div class="start-setup__row">
        <span>{{ t('common.position.x') }}</span>
        <span v-if="startPosition.handlebarX">{{
          `${startPosition.handlebarX * -1} ${t('common.unit.mm')}`
        }}</span>
        <span v-else>-</span>
      </div>
      <div class="start-setup__row">
        <span>{{ t('common.position.y') }}</span>
        <span v-if="startPosition.handlebarY">{{
          `${startPosition.handlebarY} ${t('common.unit.mm')}`
        }}</span>
        <span v-else>-</span>
      </div>
    </div>

    <div class="start-setup__crankset">
      <h3>{{ t('common.bike.crankset') }}</h3>
      <h4>{{ t('common.specs') }}</h4>
      <div class="start-setup__row">
        <span>{{ t('common.bike.crankLength') }}</span>
        <span v-if="bikeData?.CRANK_LENGTH">{{
          `${bikeData?.CRANK_LENGTH} ${t('common.unit.mm')}`
        }}</span>
        <span v-else>-</span>
      </div>
    </div>

    <div class="start-setup__pedals">
      <h3>{{ t('common.bike.pedals') }}</h3>
      <h4>{{ t('common.specs') }}</h4>
      <div class="start-setup__row">
        <span>{{ t('common.size.height') }}</span>
        <span v-if="bikeData?.PEDAL_HEIGHT">{{
          `${bikeData?.PEDAL_HEIGHT} ${t('common.unit.mm')}`
        }}</span>
        <span v-else>-</span>
      </div>
    </div>

    <div class="start-setup__bike-setup">
      <h3>{{ t('common.bike.setup') }}</h3>

      <h4>{{ t('common.distances') }}</h4>
      <div class="start-setup__small">
        {{ t('measurement.tapeMeasure') }}
      </div>

      <template v-if="bikeData && bikeSetup">
        <div class="start-setup__row">
          <span>{{ t('common.bike.saddleHeight') }}</span>
          <span>
            {{
              bikeSetup?.SADDLE_HEIGHT
                ? `${bikeSetup?.SADDLE_HEIGHT} ${t('common.unit.mm')}`
                : '-'
            }}
          </span>
        </div>

        <div class="start-setup__row">
          <span>{{ t('common.bike.saddleHandlebar') }}</span>
          <span>
            {{
              bikeSetup?.SADDLE_HANDLEBAR_DISTANCE
                ? `${bikeSetup?.SADDLE_HANDLEBAR_DISTANCE} ${t('common.unit.mm')}`
                : '-'
            }}
          </span>
        </div>

        <div class="start-setup__row">
          <span>{{ t('common.bike.saddleHandlebarDrop') }}</span>
          <span>
            {{
              bikeSetup?.SADDLE_HANDLEBAR_DROP
                ? `${bikeSetup?.SADDLE_HANDLEBAR_DROP} ${t('common.unit.mm')}`
                : '-'
            }}
          </span>
        </div>
      </template>

      <div
        class="start-setup__loader"
        v-else
      >
        <BfLoader />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { usePositionStore } from '@/store/positionStore'
import { useBikeStore } from '@/store/bikeStore'
import { BfLoader } from '@finetic/bikefitting-ui'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const positionStore = usePositionStore()
const bikeStore = useBikeStore()

const { startPosition } = storeToRefs(positionStore)
const { bikeData, bikeSetup } = storeToRefs(bikeStore)
</script>

<style lang="scss">
.start-setup {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 1.2rem;

  & > div {
    background-color: var(--color-dark-5);
    padding: 1.2rem;
    border-radius: 0.4rem;
  }

  h3 {
    margin-bottom: 0;
  }

  h4 {
    margin-top: 1.2rem;
    margin-bottom: 0.4rem;
    color: var(--color-dark-40);
  }

  &__loader {
    height: 12rem;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    font-family: 'Roboto Condensed';
    gap: 0.8rem;

    & > span:first-child {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }

    & > span:last-child {
      text-align: right;
      white-space: nowrap;
    }
  }

  &__saddle {
    grid-area: 1 / 1 / 3 / 2;
  }

  &__handlebar {
    grid-area: 1 / 2 / 3 / 3;
  }

  &__crankset {
    grid-area: 1 / 3 / 2 / 4;
  }

  &__pedals {
    grid-area: 2 / 3 / 3 / 4;
  }

  &__bike-setup {
    grid-area: 1 / 4 / 3 / 5;
  }

  &__small {
    margin: 1.2rem 0;
    font-size: 12px;
    color: var(--color-dark-40);
  }
}
</style>
